import {snowplowTrackEvent} from "utils/snowplow";
import UTIF from "utif";
import * as Api from 'utils/api/api.js';
import {IMAGE_MIME_TYPE, UID_TYPES} from "utils/constants";


const HTML_MIME_TYPE = "text/html";
const APPLICATION_HTML_MIME_TYPE = "application/html";
const TIFF_MIME_TYPE = "image/tiff";

type MimeKind = "pdf" | "html" | "image" | "other";

const getMimeKind = (mimeType: string | null | undefined): MimeKind => {
    const type = (mimeType || "").split(";")[0].trim().toLowerCase();
    if (type === "application/pdf") return "pdf";
    if (type === HTML_MIME_TYPE || type === APPLICATION_HTML_MIME_TYPE) return "html";
    if (type.startsWith(IMAGE_MIME_TYPE)) return "image";
    return "other";
}

export const isMimeTypeMismatch = (declaredMimeType: string | null | undefined, actualMimeType: string | null | undefined): boolean => {
    const actualKind = getMimeKind(actualMimeType);
    return !!declaredMimeType && actualKind !== "other" && actualKind !== getMimeKind(declaredMimeType);
}

const getImageSizeFromUrl = (
    url: string
): Promise<{ height: number; width: number }> => {
    return new Promise((resolve, reject) => {
        // Use the global Image constructor, not the React component
        const img = new window.Image();

        img.onload = () => {
            resolve({height: img.naturalHeight || img.height, width: img.naturalWidth || img.width});
        };

        img.onerror = (err) => {
            reject(err);
        };

        img.src = url;
    });
};

export async function fetchAttachmentBlob(url: string, mimeType: string, userData: any, uid: string | null, docVersion: string | number | null, docId: Array<number> | number | string): Promise<string | null | {url: string, height: number, width: number}> {

    let objectURL: string = "";
    let result: string | null | {url: string, height: number, width: number} = objectURL;
    let startTime = new Date().getTime();

    try {
        let attachmentBlob: Blob | undefined = undefined;

            if (!uid || uid === "") {
                return null;
            }
            let approvalResponse = null;
            if (uid?.includes(UID_TYPES.TASK)) {
                approvalResponse = await Api.getTaskAttachments(uid, docId);
            } else if (uid?.includes(UID_TYPES.DOCUMENT)) {
                approvalResponse = await Api.getDocumentAttachments(uid, docId, docVersion);
            } else if (uid?.includes(UID_TYPES.PROCESS)) {
                approvalResponse = await Api.getProcessAttachments(uid, docId);
            }
            if(approvalResponse?.urls[0].error) {
                throw new Error(`Error fetching attachment URL: ${approvalResponse?.urls[0].error}`);
            }

            let apiStorageUrl = getUrlWithCorsParameter(approvalResponse?.urls[0].url);

            let dataStorageReply = await fetch(apiStorageUrl);
            attachmentBlob = await dataStorageReply?.blob();

        if (attachmentBlob) {
            if (isMimeTypeMismatch(mimeType, attachmentBlob.type)) {
                throw new Error(`Attachment declared as ${mimeType} but storage returned ${attachmentBlob.type}`);
            }
            if (attachmentBlob.type === TIFF_MIME_TYPE) {
                let arrayBufferTiff = await attachmentBlob.arrayBuffer();
                let decodedTiff = UTIF.decode(arrayBufferTiff);
                UTIF.decodeImage(arrayBufferTiff, decodedTiff[0]);
                let imageObject = await UTIF.toRGBA8(decodedTiff[0]);
                imageObject.width = decodedTiff[0].width;
                imageObject.height = decodedTiff[0].height;

                result = imageObject;
            } else {
                const encodedBlob = (mimeType === HTML_MIME_TYPE || mimeType === APPLICATION_HTML_MIME_TYPE) ? new Blob([attachmentBlob], {type: HTML_MIME_TYPE + ';charset=utf-8'}) : attachmentBlob;
                objectURL = URL.createObjectURL(encodedBlob);

                if (attachmentBlob.type.includes(IMAGE_MIME_TYPE) ) {
                   const sizeResponse=await getImageSizeFromUrl(objectURL);

                   result = { url: objectURL, height: sizeResponse.height, width: sizeResponse.width};
                } else {
                   result = objectURL;
                }
            }
        }

    } catch (error) {
        if (error instanceof Error)
            console.log("Could not get the attachments url " + error.message);
        throw error;
    }
    let stopTime = new Date().getTime();
    let total = ((stopTime - startTime) / 1000).toString() || "unknown";

    snowplowTrackEvent({
        category: "bypass-cir",
        action: "fetch-attachment",
        label: "fetch-attachment",
        value: total
    }, userData);

    return result;
}

function getUrlWithCorsParameter(url: string | undefined): string | undefined {
    let apiStorageUrl = url;
    if (typeof apiStorageUrl === "string") {
        try {
            const hostname = new URL(apiStorageUrl).hostname;
            if (hostname.endsWith(".vismaonline.com")) {
                apiStorageUrl += "?corsOrigin=" + encodeURIComponent(window.location.origin);
            }
        } catch (e) {
            // Invalid URL, do nothing
        }
    }
    return apiStorageUrl;
}

export default fetchAttachmentBlob
